import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Stack, TextInput, Textarea } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconEdit } from '@tabler/icons-react';
import { useState } from 'react';
import {
  bulkUpdateProductDetailsDocument,
  countActiveCartsForProductDocument,
} from '../graphql/mutations';

interface Props {
  productId: string;
  code: string;
  currentName: string;
  currentDescription: string | null;
}

export const BulkProductDetailsEditModal = ({
  productId,
  code,
  currentName,
  currentDescription,
}: Props) => {
  const [name, setName] = useState<string>(currentName);
  const [description, setDescription] = useState<string>(currentDescription ?? '');

  const { data: cartsData } = useQuery(countActiveCartsForProductDocument, {
    variables: { productId },
    fetchPolicy: 'network-only',
  });

  const affectedCarts = cartsData?.countOrders ?? 0;

  const [bulkUpdate, { loading }] = useMutation(bulkUpdateProductDetailsDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
    notifyOnNetworkStatusChange: true,
  });

  const trimmedName = name.trim();
  const normalisedDescription = description.trim().length > 0 ? description : null;

  const unchanged = trimmedName === currentName && normalisedDescription === currentDescription;
  const nameMissing = trimmedName.length === 0;

  const handleSubmit = async () => {
    const result = await bulkUpdate({
      variables: {
        productId,
        newName: trimmedName,
        newDescription: normalisedDescription,
      },
    });

    const count = result.data?.bulkUpdateProductDetails ?? 0;

    notifications.show({
      message: `Detaily upravené pre ${count} riadkov v košíkoch`,
      color: 'green',
    });

    modals.closeAll();
  };

  return (
    <Stack>
      <Alert color="blue" variant="light">
        Produkt <strong>{code}</strong> · {affectedCarts} prebiehajúcich košíkov (CREATED) získa
        nový názov a popis aj v riadkoch objednávky. Zákazníci nedostanú notifikáciu.
      </Alert>
      <TextInput
        label="Názov"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        autoFocus
      />
      <Textarea
        label="Popis"
        autosize
        minRows={2}
        maxRows={6}
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
      />
      <Button
        leftSection={<IconEdit size={16} />}
        loading={loading}
        disabled={unchanged || nameMissing}
        onClick={handleSubmit}
      >
        Upraviť detaily
      </Button>
    </Stack>
  );
};
